import { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { ELO_LEVELS } from '../../../engine';
import { eloTimeline, eloTrend, nextLevelInfo } from '../eloData';
import { Card, EmptyState, LevelChip, SectionTitle, lastNameOf } from '../mobileUi';
import { useLocale, useT } from '../../../i18n';

export interface MobileEloProps {
  member: any;
  effectivePlayerDB: any[];
  singleMatches: any[];
  /** getPlayerTournamentHistory(member) — every tournament match with its ELO change. */
  history: any[];
}

// «ELO» — what the rating is, how far to the next level, the trend, and the
// changes behind it. Opened from the level card on Hjem (Profil stays Profil).
export function MobileElo({ member, effectivePlayerDB, singleMatches, history }: MobileEloProps) {
  const { t, formatDate } = useLocale();
  const db = effectivePlayerDB || [];
  const elo = Math.round(member?.elo || 0);
  const info = nextLevelInfo(elo);
  const events = useMemo(
    () => eloTimeline({ member, singleMatches, history }),
    [member, singleMatches, history],
  );
  const now = Date.now();
  const last30 = eloTrend(events, now - 30 * 86400000);
  const last90 = eloTrend(events, now - 90 * 86400000);
  const recent = [...events].reverse().slice(0, 12);
  const series = events.slice(-24);

  if (!member) {
    return (
      <div className="px-4 pt-6 text-center text-ink-3 text-[13px]">{t('mobile.elo.noPlayer')}</div>
    );
  }

  const Trend = ({ v }: { v: number }) => (
    <span
      className="inline-flex items-center gap-1 font-mono font-bold text-[13px]"
      style={{ color: v > 0 ? '#3f6212' : v < 0 ? '#be123c' : '#6b746d' }}
    >
      {v > 0 ? <TrendingUp size={14} /> : v < 0 ? <TrendingDown size={14} /> : <Minus size={14} />}
      {v > 0 ? '+' : ''}
      {v}
    </span>
  );

  return (
    <div className="px-4 pb-4 space-y-4">
      <h1
        className="font-display font-semibold text-ink leading-tight pt-2"
        style={{ fontSize: 26 }}
      >
        {t('mobile.elo.title')}
      </h1>

      {/* Rating + next level */}
      <div
        className="rounded-3xl p-4 relative overflow-hidden"
        style={{ background: '#0f1611', color: '#fff' }}
      >
        <div
          className="absolute -right-8 -top-8 rounded-full"
          style={{ width: 140, height: 140, background: info.current.color, opacity: 0.35 }}
        />
        <div className="relative">
          <div className="flex items-end justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider opacity-70">ELO</div>
              <div
                className="font-mono font-bold tabular-nums leading-none"
                style={{ fontSize: 40 }}
              >
                {elo}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-wider opacity-70">
                {t('mobile.elo.level')}
              </div>
              <div
                className="font-display font-semibold leading-none mt-1"
                style={{ fontSize: 30 }}
              >
                N{info.current.level}
              </div>
              <div className="text-[12px] opacity-90">{info.current.name}</div>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] opacity-80 mb-1">
              <span>
                N{info.current.level} · {info.current.min}
              </span>
              <span>
                {info.next
                  ? `N${info.next.level} · ${info.next.min}`
                  : t('mobile.elo.topLevelShort')}
              </span>
            </div>
            <div
              className="rounded-full overflow-hidden"
              style={{ height: 8, background: 'rgba(255,255,255,0.18)' }}
            >
              <div
                style={{
                  width: `${Math.round(info.progress * 100)}%`,
                  height: '100%',
                  background: '#c2f02f',
                }}
              />
            </div>
            <div className="text-[13px] font-semibold mt-2">
              {info.next
                ? t('mobile.elo.pointsToNext', {
                    points: info.pointsToNext,
                    level: `N${info.next.level}`,
                    name: info.next.name,
                  })
                : t('mobile.elo.topLevel')}
            </div>
          </div>
        </div>
      </div>

      {/* Trend */}
      <div>
        <SectionTitle>{t('mobile.elo.trend')}</SectionTitle>
        <Card>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3">
                {t('mobile.elo.days30')}
              </div>
              <Trend v={last30} />
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3">
                {t('mobile.elo.days90')}
              </div>
              <Trend v={last90} />
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3">
                {t('mobile.common.matches')}
              </div>
              <span className="font-mono font-bold text-[13px] text-ink">{events.length}</span>
            </div>
          </div>
          {series.length >= 2 ? (
            <Sparkline points={series.map((e) => e.elo)} className="mt-3" />
          ) : (
            <div className="text-[11px] text-ink-3 mt-3 text-center">
              {t('mobile.elo.noTrendYet')}
            </div>
          )}
        </Card>
      </div>

      {/* Recent changes */}
      <div>
        <SectionTitle>{t('mobile.elo.recentChanges')}</SectionTitle>
        {recent.length === 0 ? (
          <EmptyState title={t('mobile.elo.emptyTitle')} hint={t('mobile.elo.emptyHint')} />
        ) : (
          <div className="space-y-1.5">
            {recent.map((e, i) => (
              <Card key={`${e.ts}-${i}`} className="!py-2.5">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-ink truncate">{e.label}</div>
                    <div className="text-[11px] text-ink-3 truncate">
                      {e.ts
                        ? formatDate(new Date(e.ts), {
                            day: 'numeric',
                            month: 'short',
                          })
                        : ''}
                      {e.opponents.length
                        ? t('mobile.elo.versus', {
                            names: e.opponents.map((id) => lastNameOf(db, id, t)).join('/'),
                          })
                        : ''}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div
                      className="font-mono font-bold text-[14px]"
                      style={{ color: e.delta > 0 ? '#3f6212' : '#be123c' }}
                    >
                      {e.delta > 0 ? '+' : ''}
                      {Math.round(e.delta)}
                    </div>
                    <div className="font-mono text-[10px] text-ink-3 tabular-nums">→ {e.elo}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Level table */}
      <div>
        <SectionTitle>{t('mobile.elo.levels')}</SectionTitle>
        <div className="space-y-1">
          {ELO_LEVELS.map((l) => {
            const active = l.id === info.current.id;
            return (
              <div
                key={l.id}
                className="flex items-center gap-2 rounded-xl px-3 py-1.5"
                style={{
                  background: active ? '#fff' : 'transparent',
                  border: `1px solid ${active ? '#c2f02f' : 'transparent'}`,
                }}
              >
                <LevelChip elo={l.min} compact />
                <span className="text-[12px] font-semibold text-ink flex-1 truncate">{l.name}</span>
                <span className="font-mono text-[10px] text-ink-3 tabular-nums">
                  {l.min}–{l.max === 9999 ? '∞' : l.max}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-ink-3 mt-2 px-1 flex items-start gap-1.5">
          <Info size={12} className="flex-shrink-0 mt-0.5" />
          {t('mobile.elo.explainer')}
        </p>
      </div>
    </div>
  );
}

/** Tiny inline line chart — no library, scales to the card width. */
function Sparkline({ points, className = '' }: { points: number[]; className?: string }) {
  const t = useT();
  const w = 300;
  const h = 64;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const xy = points.map((p, i) => [i * step, h - 6 - ((p - min) / span) * (h - 12)]);
  const d = xy
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');
  const last = xy[xy.length - 1];
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={`w-full ${className}`}
      style={{ height: 64 }}
      aria-label={t('mobile.elo.chartAria')}
    >
      <path d={d} fill="none" stroke="#0f1611" strokeWidth={2} strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={4} fill="#c2f02f" stroke="#0f1611" strokeWidth={1.5} />
      <text x={0} y={h - 1} fontSize={9} style={{ fill: 'var(--ink-3)' }} fontFamily="monospace">
        {min}
      </text>
      <text
        x={w}
        y={9}
        fontSize={9}
        style={{ fill: 'var(--ink-3)' }}
        fontFamily="monospace"
        textAnchor="end"
      >
        {max}
      </text>
    </svg>
  );
}
